/**
 * DataRefineryPage: the tests of the latest scoring night and the proven
 * tasks generated from them, for one benchmark of the team.
 *
 * Task generation writes tasks from the team's failure records and
 * benchmark tests, and the gate proves each one (TRA-1477). The api places
 * every proven task under the test it came from (/api/proven-tasks/tree),
 * so the page can say, test by test, how the served model has been doing,
 * what the failure analysis judged it, what the base policy scored before
 * any training step and what generation made of it. The Show menu's
 * verdicts are the failure analysis's review queues. The benchmark pick
 * lives in the URL. The Show menu and the selection start over when the
 * pick changes, so the tree
 * section is keyed by the benchmark. Team-scoped: the api reads only the caller's
 * own bucket prefix. Staff on their own team act on the selection
 * (RefineryActions), which renders nothing for anyone else, and only they
 * see the staff requests a generation start cannot read, since only they
 * can fix one (useStaffTeam).
 */

import { useState } from 'react'
import { Filter } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'

import { getApiErrorDetail } from '@/api/client'
import { withSearchParam } from '@/components/models/home/requestLogOptions'
import { BenchmarkSelect } from '@/components/refinery/BenchmarkSelect'
import { RefineryActions } from '@/components/refinery/RefineryActions'
import { RefineryTree, type Selection, UnresolvedTasks } from '@/components/refinery/RefineryTree'
import { ShowMenu } from '@/components/refinery/ShowMenu'
import { useStaffTeam } from '@/components/refinery/useStaffTeam'
import {
  REFINERY_COPY,
  shownTestsLabel,
  treeSummaryLabel,
  unreadableRequestsLabel,
} from '@/components/refinery/rows'
import {
  NOTHING_TICKED,
  type Ticked,
  type TreeFilter,
  defaultBenchmark,
  filterCounts,
  isFiltered,
  visibleTasks,
  visibleTests,
} from '@/components/refinery/tree'
import { LoadingPanel } from '@/components/shared/LoadingScreen'
import { EmptyState } from '@/components/ui/empty-state'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { useMinimumLoading } from '@/hooks/useMinimumSplash'
import { useRefineryBenchmarks, useRefineryTree } from '@/hooks/useRefineryTree'
import type { RefineryTreeResponse } from '@/api/generated/model'

/** The tree's counts and its nightly model, or how many tests the Show menu leaves. */
function Summary({ tree, filter, shown }: { tree: RefineryTreeResponse; filter: TreeFilter; shown: number }): React.JSX.Element {
  if (isFiltered(filter)) return <span>{shownTestsLabel(shown, tree.counts.tests)}</span>
  return (
    <span>
      {treeSummaryLabel(tree.counts.tests, tree.counts.tasks)}
      {tree.model !== null && (
        <>
          {' '}
          {REFINERY_COPY.nightlyModel} <span className="font-mono">{tree.model}</span>.
        </>
      )}
    </span>
  )
}

/** Everything under the header for one benchmark; remounted when the pick changes. */
function TreeSection({ tree }: { tree: RefineryTreeResponse }): React.JSX.Element {
  const [filter, setFilter] = useState<TreeFilter>('all')
  const [ticked, setTicked] = useState<Ticked>(NOTHING_TICKED)
  const staff = useStaffTeam() !== null
  if (tree.night === null && tree.counts.tasks === 0) {
    return (
      <EmptyState
        icon={Filter}
        title={REFINERY_COPY.noNightTitle}
        description={tree.no_night_reason ?? undefined}
      />
    )
  }
  const tests = visibleTests(tree.tests, filter)
  const unresolved = visibleTasks(tree.unresolved, filter)
  const selection: Selection = { ticked, onChange: setTicked }
  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <ShowMenu
          value={filter}
          onChange={setFilter}
          tests={tree.counts.tests}
          counts={filterCounts(tree.tests, tree.unresolved)}
        />
        <p className="ml-auto text-xs text-muted-foreground">
          <Summary tree={tree} filter={filter} shown={tests.length} />
        </p>
      </div>
      {tree.no_night_reason !== null && (
        <p className="text-sm text-muted-foreground">{tree.no_night_reason}</p>
      )}
      {staff && tree.unreadable_requests.length > 0 && (
        <p role="alert" className="text-sm text-destructive">
          {unreadableRequestsLabel(tree.unreadable_requests)}
        </p>
      )}
      <RefineryActions
        tree={tree}
        ticked={ticked}
        onDone={() => {
          setTicked(NOTHING_TICKED)
        }}
      />
      {isFiltered(filter) && tests.length === 0 && unresolved.length === 0 && (
        <p className="text-sm text-muted-foreground">{REFINERY_COPY.noMatch}</p>
      )}
      {tests.length > 0 && <RefineryTree tests={tests} selection={selection} />}
      {unresolved.length > 0 && <UnresolvedTasks tasks={unresolved} selection={selection} />}
    </section>
  )
}

export default function DataRefineryPage(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const benchmarks = useRefineryBenchmarks()
  const known = benchmarks.data?.benchmarks ?? []
  const benchmark = searchParams.get('benchmark') ?? defaultBenchmark(known)
  const tree = useRefineryTree(benchmark)
  const loading = useMinimumLoading(benchmarks.isLoading || tree.isLoading)
  const isError = benchmarks.isError || tree.isError
  const error = benchmarks.error ?? tree.error

  function pickBenchmark(next: string): void {
    setSearchParams((prev) => withSearchParam(prev, 'benchmark', next), { replace: true })
  }

  return (
    <PageContainer className="space-y-6">
      <PageHeader
        title={REFINERY_COPY.title}
        description={REFINERY_COPY.description}
        actions={
          benchmark !== undefined && known.length > 0 ? (
            <BenchmarkSelect benchmarks={known} value={benchmark} onChange={pickBenchmark} />
          ) : undefined
        }
      />

      {loading && !isError && <LoadingPanel />}

      {isError && (
        <p className="text-sm text-destructive">
          {REFINERY_COPY.loadFailed} {getApiErrorDetail(error) ?? 'unknown error'}
        </p>
      )}

      {!loading && !isError && benchmarks.data !== undefined && known.length === 0 && (
        <EmptyState icon={Filter} title={REFINERY_COPY.noBenchmarks} />
      )}

      {!loading && !isError && tree.data !== undefined && (
        <TreeSection key={tree.data.benchmark} tree={tree.data} />
      )}
    </PageContainer>
  )
}
